import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { masterSupabase as supabase } from '../lib/supabase-master';
import { ReportViewer } from '../components/BankReportInteractive';

/**
 * InteractiveReportPage
 * Full-screen interactive presentation page
 * 
 * Features:
 * - Load report data from Supabase
 * - Password protection (optional)
 * - Error handling
 * - Loading states
 * - Analytics tracking
 */
const InteractiveReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');

  // Load report data
  useEffect(() => {
    if (!reportId) {
      setError('Keine Report-ID angegeben');
      setIsLoading(false);
      return;
    }

    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch report from Supabase
      const { data, error: fetchError } = await supabase
        .from('interactive_reports')
        .select('*')
        .eq('id', reportId)
        .single();

      if (fetchError) {
        throw new Error('Report nicht gefunden');
      }

      // Check if password is required
      if (data.password_hash && !password) {
        setPasswordRequired(true);
        setIsLoading(false);
        return;
      }

      // Check expiration
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        throw new Error('Dieser Report ist abgelaufen');
      }

      // Increment view count
      await supabase
        .from('interactive_reports')
        .update({ views_count: (data.views_count || 0) + 1 })
        .eq('id', reportId);

      setReportData(data);
      setIsLoading(false);

      console.log('[REPORT] Loaded successfully:', reportId);
    } catch (err) {
      console.error('[REPORT] Error loading:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    await loadReport();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-purple-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Lade Präsentation...
          </h2>
          <p className="text-gray-600">
            Bitte einen Moment Geduld
          </p>
        </div>
      </div>
    );
  }

  // Password required
  if (passwordRequired) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Passwort erforderlich
            </h2>
            <p className="text-gray-600">
              Dieser Report ist passwortgeschützt
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort eingeben"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
              autoFocus
            />
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all duration-200"
            >
              Öffnen
            </button>
          </form>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-red-50 to-orange-50">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Fehler beim Laden
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/clara-ki')}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200"
          >
            Zurück zu Clara
          </button>
        </div>
      </div>
    );
  }

  // Render report
  return (
    <div className="w-full h-screen">
      <ReportViewer 
        reportData={reportData} 
        reportId={reportId}
      />
    </div>
  );
};

export default InteractiveReportPage;

